
import { mapState, mapGetters, mapActions } from 'vuex';
import useVeevaLibrary from '../../composition-api/edetailer/veeva-library';
import LoadingIndicator from "./LoadingIndicator.vue";

const EDETAILER_WIDTH = 1024;
const EDETAILER_HEIGHT = 768;

export default {
  components: {
    LoadingIndicator
  },

  data() {
    return {
      nonResponsiveInitialised: false,
      timeout: undefined,
      iframeEl: null,
    };
  },

  setup() {
    const { iframe: veevaIFrame, setupVeevaLibrary } = useVeevaLibrary();
    setupVeevaLibrary();
    return {
      veevaIFrame,
    };
  },

  computed: {
    ...mapState('chatbotStore', ['latestConversation', 'iframeUrl', 'isSlideLoading']),
    ...mapGetters('chatbotStore', ['isResponsive']),

    iframeSrc() {
      const { version } = this.latestConversation;

      return typeof version === 'number' ? `${this.iframeUrl}?v=${version}` : this.iframeUrl;
    },

    /**
     * This is to watch for both isResponsive and iframeUrl changes in the same watch handler.
     * Needed as in some cases isResponsive changes before iframeUrl and in other cases it's
     * the other way around. And we need both to properly setup non-responsive handling.
     */
    responsiveWatcher() {
      return `${this.isResponsive}_${this.iframeUrl}`;
    },
  },

  watch: {
    responsiveWatcher: {
      immediate: true,
      handler(newValue, oldValue) {
        if (!this.iframeUrl) return;

        if (this.isResponsive === false && !this.nonResponsiveInitialised) {
          this.setupNonResponsive();
        } else if (this.isResponsive === false && newValue !== oldValue) {
          this.destroyNonResponsive();
          this.setupNonResponsive();
        } else if (this.nonResponsiveInitialised) {
          this.destroyNonResponsive();
        }
      },
    },

    iframeSrc(url, oldUrl) {
      if (url !== oldUrl) {
        this.setIsSlideLoading(true);
      }
    },
  },

  mounted() {
    this.setIsSlideLoading(true);
  },

  unmounted() {
    this.destroyNonResponsive();
  },

  destroyed() {
    this.iframeEl?.removeEventListener('load', this.onIframeLoad);
  },

  methods: {
    ...mapActions('chatbotStore', ['setIsSlideLoading']),

    setIframeRefs(el) {
      this.$refs.edetail = el;
      this.veevaIFrame = el;
    },

    setupNonResponsive() {
      this.nonResponsiveInitialised = true;
      window.addEventListener('resize', this.onResize);
    },

    destroyNonResponsive() {
      this.nonResponsiveInitialised = false;
      this.scaleEDetailer(true);
      window.removeEventListener('resize', this.onResize);
    },

    onResize() {
      this.resizing = true;

      if (this.timeout) {
        this.timeout = clearTimeout(this.timeout);
      }

      this.timeout = setTimeout(() => this.$nextTick(this.scaleEDetailer), 50);
    },

    scaleEDetailer(reset = false) {
      const iframe = this.$refs.edetail.$el.querySelector('iframe');

      if (!iframe) return;

      if (reset) {
        iframe.style.transform = '';
      } else {
        const scale = 1 / Math.max(EDETAILER_WIDTH / this.$el.offsetWidth, EDETAILER_HEIGHT / this.$el.offsetHeight);
        iframe.style.transform = `translate3d(-50%, -50%, 0px) scale(${scale})`;
      }
    },

    /**
     * The "load" event of the vue-friendly-iframe component is emitted long before the
     * content of the iframe is emitted. It is emitted just as the iframe is setup to actually
     * render the correct iframe URL. Therefore we setup an event listener for the "load"
     * event on the iframe element itself.
     * @see https://github.com/officert/vue-friendly-iframe/issues/42#issuecomment-1029412066
     */
    onFriendlyIframeLoad() {
      this.iframeEl?.removeEventListener('load', this.onIframeLoad);
      this.iframeEl = this.$refs.edetail?.$el.getElementsByTagName('iframe')[0];

      if (this.iframeEl) {
        this.iframeEl.addEventListener('load', this.onIframeLoad);
      } else {
        this.onIframeLoad();
      }
    },

    onIframeLoad() {
      this.setIsSlideLoading(false);

      if (!this.isResponsive) {
        this.scaleEDetailer();
      }
    },
  },
};
